import { createContext, useContext, useState, useCallback, useEffect, forwardRef } from 'react';
import { X, AlertTriangle, CheckCircle2, Info, Loader2 } from 'lucide-react';

/* ---------------- Toast ---------------- */
const ToastCtx = createContext(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const push = useCallback((message, type = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setItems((s) => [...s, { id, message, type }]);
    setTimeout(() => setItems((s) => s.filter((i) => i.id !== id)), 5000);
  }, []);
  const toast = useCallback(
    Object.assign((m) => push(m, 'info'), {
      success: (m) => push(m, 'success'),
      error: (m) => push(m, 'error'),
      info: (m) => push(m, 'info'),
    }),
    [push]
  );
  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 no-print">
        {items.map((i) => (
          <div
            key={i.id}
            className={`flex max-w-md items-start gap-2 rounded-lg px-4 py-3 text-sm shadow-lg ring-1 ${
              i.type === 'error'
                ? 'bg-red-50 text-red-800 ring-red-200'
                : i.type === 'success'
                ? 'bg-emerald-50 text-emerald-800 ring-emerald-200'
                : 'bg-white text-slate-700 ring-slate-200'
            }`}
          >
            {i.type === 'error' ? <AlertTriangle size={18} className="mt-px shrink-0" /> :
             i.type === 'success' ? <CheckCircle2 size={18} className="mt-px shrink-0" /> :
             <Info size={18} className="mt-px shrink-0" />}
            <span>{i.message}</span>
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ---------------- Modal ---------------- */
export function Modal({ open, onClose, title, children, footer, wide = false, size }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  const w = size || (wide ? 'max-w-4xl' : 'max-w-lg');
  return (
    /* .853 Na telefóne je okno cez celú obrazovku: hlavička s krížikom hore
     * a tlačidlá (Uložiť, Odoslať…) prilepené dole – nehľadajú sa pod textom. */
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 max-md:p-0 sm:p-8 no-print">
      <div className={`w-full ${w} rounded-xl bg-white shadow-xl max-md:flex max-md:min-h-[100dvh] max-md:flex-col max-md:rounded-none`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5 max-md:sticky max-md:top-0 max-md:z-10 max-md:bg-white max-md:px-4 max-md:py-2.5">
          <h3 className="text-base font-semibold text-slate-800">{title}</h3>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 max-md:-mr-1 max-md:p-2">
            <X size={22} />
          </button>
        </div>
        <div className="px-5 py-4 max-md:flex-1 max-md:px-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3 max-md:sticky max-md:bottom-0 max-md:flex-wrap max-md:bg-white max-md:px-3 max-md:pb-[calc(0.75rem+env(safe-area-inset-bottom))]">{footer}</div>}
      </div>
    </div>
  );
}

/* ---------------- Formulárové polia ---------------- */
export function Field({ label, children, className = '', hint, required }) {
  return (
    <div className={className}>
      {label && (
        <label className="label">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {children}
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

export function Input({ label, className = '', hint, required, ...props }) {
  return (
    <Field label={label} className={className} hint={hint} required={required}>
      <input className="input" {...props} />
    </Field>
  );
}

/* `forwardRef` kvôli tomu, aby sa dal postaviť kurzor – napríklad v odpovedi
 * zo Schránky pod predvyplnené oslovenie. */
export const Textarea = forwardRef(function Textarea(
  { label, className = '', rows = 3, hint, ...props }, ref
) {
  return (
    <Field label={label} className={className} hint={hint}>
      <textarea ref={ref} className="input" rows={rows} {...props} />
    </Field>
  );
});

export function Select({ label, className = '', options = [], hint, ...props }) {
  return (
    <Field label={label} className={className} hint={hint}>
      <select className="input" {...props}>
        {options.map((o) => (
          <option key={o.value ?? o.key} value={o.value ?? o.key}>
            {o.label}
          </option>
        ))}
      </select>
    </Field>
  );
}

export const Spinner = ({ className = '' }) => <Loader2 className={`animate-spin ${className}`} size={18} />;

export function Loading({ label = 'Načítavam…' }) {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-slate-400">
      <Spinner /> {label}
    </div>
  );
}

export function Empty({ icon: Icon, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
      {Icon && <Icon size={40} className="text-slate-300" />}
      <p className="font-medium text-slate-600">{title}</p>
      {hint && <p className="max-w-md text-sm text-slate-400">{hint}</p>}
      {action}
    </div>
  );
}

/* ---------------- Stavy ---------------- */
export const ORDER_STATUS = {
  nova: { label: 'Nová', cls: 'bg-sky-100 text-sky-800' },
  potvrdena: { label: 'Potvrdená', cls: 'bg-indigo-100 text-indigo-800' },
  objednana: { label: 'Objednaná u dodávateľa', cls: 'bg-amber-100 text-amber-800' },
  pripravena: { label: 'Pripravená na odber', cls: 'bg-violet-100 text-violet-800' },
  vydana: { label: 'Vydaná', cls: 'bg-emerald-100 text-emerald-800' },
  stornovana: { label: 'Stornovaná', cls: 'bg-slate-200 text-slate-600' },
};

/** Stavy vydanej faktúry. Po splatnosti sa dofarbuje v zozname zvlášť. */
export const INVOICE_STATUS = {
  koncept: { label: 'Koncept', cls: 'bg-slate-200 text-slate-700' },
  vystavena: { label: 'Vystavená', cls: 'bg-sky-100 text-sky-800' },
  uhradena: { label: 'Uhradená', cls: 'bg-emerald-100 text-emerald-800' },
  stornovana: { label: 'Stornovaná', cls: 'bg-slate-200 text-slate-600' },
};

export const QUOTE_STATUS = {
  koncept: { label: 'Koncept', cls: 'bg-slate-200 text-slate-700' },
  odoslana: { label: 'Odoslaná', cls: 'bg-sky-100 text-sky-800' },
  prijata: { label: 'Prijatá', cls: 'bg-emerald-100 text-emerald-800' },
  vybavena: { label: 'Vybavená', cls: 'bg-teal-600 text-white' },
  zamietnuta: { label: 'Zamietnutá', cls: 'bg-red-100 text-red-700' },
  expirovana: { label: 'Expirovaná', cls: 'bg-amber-100 text-amber-800' },
};

export function StatusBadge({ status, map = ORDER_STATUS }) {
  const s = map[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
}

export function Confirm({ open, title, message, onConfirm, onClose, confirmLabel = 'Potvrdiť', danger }) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>Zrušiť</button>
          <button className={danger ? 'btn-danger' : 'btn-primary'} onClick={() => { onConfirm(); onClose(); }}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <p className="text-sm text-slate-600">{message}</p>
    </Modal>
  );
}
